/**
 * The state a finding's evidence is presented in (#966 AC2).
 *
 * Never `verification_status` alone: `application/decision/services.py:181` passes the
 * status through unchanged while withholding the claim, so `verified` + `claim: null` is
 * reachable in production. Showing "verified" there would be invariant 4 collapsing into
 * success. Worst-of is monotone — it can under-claim, never over-claim — so #968's
 * authoritative resolver can replace it without this ever having shipped a false positive.
 */
import { AlertTriangle, Ban, CheckCircle2, CircleDashed, CircleHelp, EyeOff, FileX, History, Unlink } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { VERIFICATION_STATUSES } from '../../types/evidence';
import type { FindingEvidenceAvailability, VerificationStatus } from '../../types/evidence';

export const EVIDENCE_DISPLAY_STATES = [
  ...VERIFICATION_STATUSES,
  'withheld',
  'unknown',
] as const;
export type EvidenceDisplayState = typeof EVIDENCE_DISPLAY_STATES[number];

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info';

interface StatePresentation {
  labelKey: `evidence.status.${EvidenceDisplayState}`;
  variant: BadgeVariant;
  icon: LucideIcon;
}

/**
 * Every state carries an icon AND a label — never colour alone, so the distinction
 * survives for anyone who cannot separate the two tints. `unverified` is the default of
 * nearly every adapted finding (`application/evidence/registry.py:261`), so it must look
 * unfinished rather than neutral: a neutral chip renders an AI candidate as fact
 * (invariant 1).
 */
export const EVIDENCE_STATE_PRESENTATION = {
  unverified: { labelKey: 'evidence.status.unverified', variant: 'warning', icon: CircleDashed },
  verified: { labelKey: 'evidence.status.verified', variant: 'success', icon: CheckCircle2 },
  missing: { labelKey: 'evidence.status.missing', variant: 'destructive', icon: FileX },
  stale: { labelKey: 'evidence.status.stale', variant: 'warning', icon: History },
  unsupported: { labelKey: 'evidence.status.unsupported', variant: 'outline', icon: Ban },
  conflict: { labelKey: 'evidence.status.conflict', variant: 'destructive', icon: AlertTriangle },
  withheld: { labelKey: 'evidence.status.withheld', variant: 'info', icon: EyeOff },
  unknown: { labelKey: 'evidence.status.unknown', variant: 'outline', icon: CircleHelp },
} satisfies Record<EvidenceDisplayState, StatePresentation>;

const IS_KNOWN_STATUS = new Set<string>(VERIFICATION_STATUSES);

/** Withheld outranks everything, including a status this UI has never heard of. */
export const resolveEvidenceState = (
  finding: { verificationStatus: VerificationStatus; claim: string | null },
): EvidenceDisplayState => {
  if (finding.claim === null) return 'withheld';
  return IS_KNOWN_STATUS.has(finding.verificationStatus) ? finding.verificationStatus : 'unknown';
};

interface AvailabilityPresentation {
  labelKey: `evidence.availability.${Extract<FindingEvidenceAvailability, 'partially_unavailable' | 'unavailable'>}`;
  variant: BadgeVariant;
  icon: LucideIcon;
}

/**
 * The finding-level availability badge (#1195), rendered NEXT TO the historical status,
 * never instead of it: "verified" and "sources cannot be opened" are two true statements
 * about two axes. `available` and `no_evidence` add nothing the rows do not already say.
 */
export const EVIDENCE_AVAILABILITY_PRESENTATION = {
  partially_unavailable: {
    labelKey: 'evidence.availability.partially_unavailable', variant: 'warning', icon: Unlink,
  },
  unavailable: { labelKey: 'evidence.availability.unavailable', variant: 'destructive', icon: Unlink },
} satisfies Record<'partially_unavailable' | 'unavailable', AvailabilityPresentation>;

export const resolveAvailabilityBadge = (
  finding: { evidenceAvailability: FindingEvidenceAvailability },
): AvailabilityPresentation | null => (
  finding.evidenceAvailability === 'partially_unavailable' || finding.evidenceAvailability === 'unavailable'
    ? EVIDENCE_AVAILABILITY_PRESENTATION[finding.evidenceAvailability]
    : null
);
